/** Debounce applied to keystrokes before a preview search is issued. */
export const SEARCH_PREVIEW_DEBOUNCE_MS = 300;
/** Hits fetched per index for the dropdown preview. */
export const SEARCH_PREVIEW_HITS_PER_PAGE = 3;
/** Shortest query worth sending to Algolia. */
export const SEARCH_PREVIEW_MIN_QUERY_LENGTH = 2;
/**
 * Builds an empty result set. A factory rather than a shared constant, so
 * callers can assign it to reactive state without aliasing the same arrays.
 * @returns Empty results.
 */
export function emptySearchPreviewResults() {
    return { courses: [], coursesCount: 0, instructors: [], instructorsCount: 0 };
}
/**
 * Builds the canonical course URL, absolute when the search lives off-site.
 * Only the path is lowercased, for SEO consistency.
 * @param slug - The SEO-friendly slug portion of the URL.
 * @param courseCode - The unique course code.
 * @param baseUrl - Absolute origin to prefix, empty on the website itself.
 * @returns The course URL.
 */
export function buildCoursePreviewUrl(slug, courseCode, baseUrl = '') {
    return `${baseUrl}${`/c/${slug}_${courseCode}`.toLowerCase()}`;
}
/**
 * Maps a raw Algolia course hit to the CourseHit shape used by the preview.
 * @param hit - Raw Algolia hit record.
 * @param baseUrl - Absolute origin to prefix, empty on the website itself.
 * @param attribution - Query, rank and index, so the click can be attributed.
 * @returns A CourseHit with a pre-computed URL.
 */
export function mapCourseHit(
// eslint-disable-next-line @typescript-eslint/no-explicit-any
hit, baseUrl = '', attribution = {}) {
    return {
        objectID: hit.objectID,
        title: hit.title ?? '',
        courseType: hit.courseType,
        slug: hit.slug ?? '',
        courseCode: hit.courseCode ?? '',
        url: buildCoursePreviewUrl(hit.slug ?? '', hit.courseCode ?? '', baseUrl),
        ...attribution
    };
}
/**
 * Maps a raw Algolia instructor hit to the InstructorHit shape used by the preview.
 * @param hit - Raw Algolia hit record.
 * @param baseUrl - Absolute origin to prefix, empty on the website itself.
 * @returns An InstructorHit with a pre-computed URL.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapInstructorHit(hit, baseUrl = '') {
    return {
        objectID: hit.objectID,
        fullName: hit.fullName ?? '',
        role: hit.role,
        slug: hit.slug ?? '',
        imageUrl: hit.imageUrl?.url ?? hit.imageUrl,
        url: `${baseUrl}/docenti/${hit.slug}`
    };
}
/**
 * Runs the multi-index preview search, batching both indices into one API call.
 * Rejects on transport errors — callers decide how to surface the failure.
 *
 * This is one of the two sanctioned direct Algolia calls in this package: the
 * InstantSearch equivalent would mount a search instance in the navbar of every
 * page of every app to fetch six hits. See the README.
 *
 * @param query - The search query string.
 * @param config - Client, indices and URL settings.
 * @returns The mapped courses and instructors with their total hit counts.
 */
export async function fetchSearchPreview(query, config) {
    const { client, coursesIndexName, instructorsIndexName, baseUrl = '', hitsPerPage = SEARCH_PREVIEW_HITS_PER_PAGE } = config;
    const { results } = await client.searchForHits({
        requests: [
            // `clickAnalytics` is what makes Algolia return a queryID. Without it the
            // dropdown — a real search, on every page of the site — produces clicks
            // that can never be attributed. It costs nothing and identifies a query,
            // not a person; sending the click itself stays behind the consent gate.
            // Tagged so its conversions can be told apart from `/esplora`'s in the
            // reports — the dropdown attributes conversions like any other search,
            // and an untagged surface is one that cannot be isolated afterwards.
            {
                indexName: coursesIndexName,
                query,
                hitsPerPage,
                clickAnalytics: true,
                analyticsTags: ['search-preview']
            },
            { indexName: instructorsIndexName, query, hitsPerPage }
        ]
    });
    const courseResults = results[0];
    const instructorResults = results[1];
    const queryID = courseResults?.queryID;
    return {
        courses: (courseResults?.hits ?? []).map((hit, i) => mapCourseHit(hit, baseUrl, { queryID, position: i + 1, index: coursesIndexName })),
        coursesCount: courseResults?.nbHits ?? 0,
        instructors: (instructorResults?.hits ?? []).map((hit) => mapInstructorHit(hit, baseUrl)),
        instructorsCount: instructorResults?.nbHits ?? 0
    };
}
